import { useLazyQuery } from "@apollo/client";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import DoneIcon from "@mui/icons-material/Done";
import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined";
import SearchIcon from "@mui/icons-material/Search";
import SwapVertIcon from "@mui/icons-material/SwapVert";
import TagIcon from "@mui/icons-material/Tag";
import ViewAgendaOutlinedIcon from "@mui/icons-material/ViewAgendaOutlined";
import ViewCozyOutlinedIcon from "@mui/icons-material/ViewCozyOutlined";
import {
  Badge,
  Box,
  Button,
  Divider,
  Fab,
  FormControl,
  Grid,
  InputAdornment,
  Stack,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import {
  fetchAllMultislotContentList,
  fetchAllMultislotContentListWithSortFilter,
  fetchCategoryCounts,
  fetchTagListing,
} from "@platformx/authoring-apis";
import { searchIcon } from "@platformx/shared/static-assets";
import { BackArrow, Loader, ShowToastError, capitalizeFirstLetter } from "@platformx/utilities";
import { Fragment, useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import GalleryItems from "../ContentGallery/GalleryItems";
import useDyanamicGalleryStyle from "./DynamicContentGallery.styles";
import GalleryFilterChips from "./GalleryFilterChips";
import GalleryFilterModal from "./GalleryFilterModal";
import GalleryItemSearchResults from "./GalleryItemSearchResult";
import GalleryLeftSideBar from "./GalleryLeftSideBar";
import GalleryListItems from "./GalleryListItems";
import GallerySortModal from "./GallerySortModal";
import { GALLERY_VIEW_MODES } from "./utils/constant";
import { ContentProps } from "./utils/contentGalleryTypes";
import {
  DEFAULT_GALLERY_FILTER,
  GalleryFilterValue,
  GallerySortValue,
  buildAdvancedVariables,
  countActiveFilters,
  hasAdvancedQuery,
} from "./utils/gallerySortFilter";
import { isSameGalleryItem } from "./utils/matchGalleryItem";

type DynamicContentGalleryProps = {
  prelemId?: any;
  multi_select?: boolean;
  handleSelectedContent?: any;
  onToggleContentGallery?: any;
  single_select?: any;
  selectedItems?: any;
  name?: string;
  allowed_content?: any;
  allowed_collection?: any;
  pageFrom?: string;
  selected_filter?: any;
  allowEmptySelection?: boolean;
  // if true, allowed_content has 5 types, but only 3 of them available then stripe remaining 2.
  restrictToAvailableCDPTags?: boolean;
};

/**
 *
 * @param arr array
 * @param multi_select boolean
 * @returns array
 */
const customPairAdded = (arr, multi_select, single_select) => {
  if (multi_select || single_select) {
    const newArray = arr.map((ele) => {
      return {
        ...ele,
        is_checked: false,
      };
    });
    return newArray;
  }
  return arr;
};

const DynamicContentGallery = (props: DynamicContentGalleryProps) => {
  const {
    prelemId,
    multi_select = false,
    handleSelectedContent,
    onToggleContentGallery,
    single_select = false,
    selectedItems = [],
    name,
    allowed_content,
    allowed_collection,
    selected_filter,
    allowEmptySelection = false,
    restrictToAvailableCDPTags = false,
  } = props;
  const filter = "ALL";
  const rows = 40;

  const { t } = useTranslation();
  const classes = useDyanamicGalleryStyle();
  const theme = useTheme();

  const [startIndex, setStartIndex] = useState<number>(0);
  const [viewMode, setViewMode] = useState<string>(GALLERY_VIEW_MODES.THUMBNAIL);
  const [sortValue, setSortValue] = useState<GallerySortValue | null>(null);
  const [filterValue, setFilterValue] = useState<GalleryFilterValue>({
    ...DEFAULT_GALLERY_FILTER,
  });
  const [isSortOpen, setSortOpen] = useState<boolean>(false);
  const [isFilterOpen, setFilterOpen] = useState<boolean>(false);
  const sortValueRef = useRef<GallerySortValue | null>(null);
  const filterValueRef = useRef<GalleryFilterValue>({ ...DEFAULT_GALLERY_FILTER });
  const isSortFilterUnsupportedRef = useRef<boolean>(false);
  const [isSortFilterUnsupported, setIsSortFilterUnsupported] = useState<boolean>(false);
  const [isLazyLoad, setIsLazyLoad] = useState<boolean>(false);
  const [isHideSearchBox, setHideSearchBox] = useState<boolean>(false);
  const [isShowItems, setShowItems] = useState<boolean>(true);
  const [isShowCategory, setShowCategory] = useState<boolean>(false);
  const [tagData, setTagData] = useState<any>({});
  const [items, setItems] = useState<ContentProps[]>([]);
  const [searchItems, setSearchItems] = useState<ContentProps[]>([]);
  const [isSuggestionEnabled, setSuggestionEnabled] = useState<boolean>(false);
  const [loading, setLoading] = useState(true);

  const selectedItemsMultiSelect = useRef<any>(
    [...selectedItems].reduce((deduped: any[], item: any) => {
      const alreadyIncluded = deduped.some((existing) => isSameGalleryItem(existing, item));
      if (!alreadyIncluded) deduped.push({ ...item, is_checked: true });
      return deduped;
    }, []),
  );

  // The gallery emits {searchTerm, selectedCDPFilter, selectedTags}, but the page store persists
  // them as dynamic_prelem_setting.selected_filter {search, selected_cdp_tags, selected_tags}
  // (updateDynamicSchemaPrelemContent). Accept both so a saved query is restored on reopen.
  // Some prelems keep a JSON object string in `search` - never treat that as a search term.
  const storedSearch =
    typeof selected_filter?.search === "string" && !selected_filter.search.trim().startsWith("{")
      ? selected_filter.search
      : "";
  const searchTerm = selected_filter?.searchTerm ?? storedSearch;
  const selectedCDPTagsFromContent =
    selected_filter?.selectedCDPFilter ?? selected_filter?.selected_cdp_tags ?? [];
  const selectedTagsFromContent =
    selected_filter?.selectedTags ?? selected_filter?.selected_tags ?? [];
  const [search, setSearch] = useState(searchTerm || "");
  const [allowedCDPTags, setAllowedCDPTags] = useState<any>([]);
  const [selectedCDPTags, setSelectedCDPTags] = useState<any>(selectedCDPTagsFromContent || []);
  const selectedCDPTagsRef = useRef<any>(selectedCDPTagsFromContent || []);
  const [selectedTags, setSelectedTags] = useState<any>(selectedTagsFromContent || []);
  const selectedTagsRef = useRef<any>(selectedTagsFromContent || []);
  //End New Additions

  const [fetchMultiSlotContentList] = useLazyQuery(fetchAllMultislotContentList);
  const [fetchMultiSlotContentListWithSort] = useLazyQuery(
    fetchAllMultislotContentListWithSortFilter,
  );
  const [runFetchCategoryCounts] = useLazyQuery(fetchCategoryCounts);

  const runContentFetch = (variables: any, allowFallback = true): Promise<any> => {
    const advanced = hasAdvancedQuery(sortValueRef.current, filterValueRef.current);
    if (!advanced || isSortFilterUnsupportedRef.current) {
      return fetchMultiSlotContentList({ variables });
    }

    const advancedVariables = {
      ...variables,
      ...buildAdvancedVariables(sortValueRef.current, filterValueRef.current),
    };

    const fallback = (reason: any) => {
      console.error(
        "DynamicContentGallery: gateway rejected the sort/filter arguments on " +
          "authoring_getDynamicContentSearch. Falling back to the unsorted query.",
        { variables: advancedVariables, error: reason },
      );
      isSortFilterUnsupportedRef.current = true;
      setIsSortFilterUnsupported(true);
      ShowToastError(t("sort_filter_unsupported"), 0);
      sortValueRef.current = null;
      filterValueRef.current = { ...DEFAULT_GALLERY_FILTER };
      setSortValue(null);
      setFilterValue({ ...DEFAULT_GALLERY_FILTER });
      return fetchMultiSlotContentList({ variables });
    };

    return fetchMultiSlotContentListWithSort({ variables: advancedVariables })
      .then((res: any) => {
        const rejected = Boolean(res?.error) && !res?.data?.authoring_getDynamicContentSearch;
        return rejected && allowFallback ? fallback(res.error) : res;
      })
      .catch((error: any) => {
        if (!allowFallback) throw error;
        return fallback(error);
      });
  };

  function debounce(func, timeout = 500) {
    let timer;
    return (...args) => {
      clearTimeout(timer);
      timer = setTimeout(() => {
        func(...args);
      }, timeout);
    };
  }
  // Content Type / Collection are small, bounded taxonomies this component needs the actual tag
  // names for (not just a count) - getAvailableContentAndCollectionTags below intersects them
  // with allowed_content/allowed_collection to restrict the initial CDP tag selection to tags
  // that genuinely exist. Every other category's tags are no longer fetched here at all - each
  // category's own GalleryLeftSideBar now loads its own listing directly via fetchTagListing,
  // the same per-category strategy @platformx/tag-selector's TagSelector uses.
  const CDP_RESTRICTION_CATEGORIES = ["Content Type", "Collection"];

  const getTagData = () => {
    runFetchCategoryCounts()
      .then(async (res) => {
        const counts = res?.data?.authoring_categoryCounts || {};
        // Content Type / Collection are config-driven (from this field's own allowed_content/
        // allowed_collection), not author-assigned tags, so authoring_categoryCounts' own tag
        // taxonomy doesn't necessarily enumerate them at all - always keep them in the rendered
        // category list regardless of whether this response happens to include them, so the
        // old "always shown at the top, non-deselectable" behavior survives even when the
        // gateway's count map omits them.
        const categories = Array.from(
          new Set([...CDP_RESTRICTION_CATEGORIES, ...Object.keys(counts)]),
        );

        const restrictionEntries = await Promise.all(
          categories
            .filter((category) => CDP_RESTRICTION_CATEGORIES.includes(category))
            .map((category) =>
              fetchTagListing({ searchCategory: category, searchString: "", start: 0, rows: 1000 })
                .then((tagRes: any) => ({
                  category,
                  tags: Array.isArray(tagRes?.authoring_getTagsList)
                    ? tagRes.authoring_getTagsList
                        .map((record: any) => record?.tag_name || record?.tag_value || "")
                        .filter(Boolean)
                    : [],
                }))
                .catch(() => ({ category, tags: [] })),
            ),
        );
        const restrictionTagsByCategory: Record<string, string[]> = Object.fromEntries(
          restrictionEntries.map(({ category, tags }) => [category, tags]),
        );

        setTagData(
          categories.map((category) => ({
            category,
            tags: restrictionTagsByCategory[category] || [],
          })),
        );
      })
      .catch((err) => {
        console.error(JSON.stringify(err, null, 2));
      });
  };

  const backToContentHandel = () => {
    setHideSearchBox(false);
  };
  const mobileSearchIconHandel = () => {
    setHideSearchBox(true);
  };

  const getInitialContentAndCollectionTags = () => {
    const allowedData = [
      ...(Array.isArray(allowed_content) && allowed_content.length > 0 ? allowed_content : []),
      ...(Array.isArray(allowed_collection) && allowed_collection.length > 0
        ? allowed_collection
        : []),
    ];
    return allowedData?.length > 0 ? allowedData : [];
  };

  // Subset of getInitialContentAndCollectionTags() limited to types that actually exist in the
  // tag taxonomy (Content Type / Collection categories) AND are allowed. These are exactly the
  // tags GalleryLeftSideBar renders as chips, so restricting the default selection to this set
  // keeps the saved filter aligned with what the user can see and toggle.
  const getAvailableContentAndCollectionTags = () => {
    if (!Array.isArray(tagData)) return [];
    const findTags = (categoryName: string) =>
      tagData.find((c) => c?.category?.toLowerCase() === categoryName)?.tags || [];

    const availableContent =
      Array.isArray(allowed_content) && allowed_content.length > 0
        ? findTags("content type").filter((tag) => allowed_content.includes(tag))
        : [];
    const availableCollection =
      Array.isArray(allowed_collection) && allowed_collection.length > 0
        ? findTags("collection").filter((tag) => allowed_collection.includes(tag))
        : [];
    return [...availableContent, ...availableCollection];
  };

  const applyCDPTags = (nextTags: string[]) => {
    setSelectedCDPTags(nextTags);
    selectedCDPTagsRef.current = nextTags;
  };

  const applyTags = (nextTags: string[]) => {
    setSelectedTags(nextTags);
    selectedTagsRef.current = nextTags;
  };

  /**
   *save handle
   */
  const handleDone = () => {
    const selectedFilter = {
      searchTerm: search,
      selectedCDPFilter: selectedCDPTagsRef.current,
      selectedTags: selectedTagsRef.current,
    };
    if (multi_select || single_select) {
      // For multi-select, use the accumulated ref rather than the currently-loaded `items`:
      // changing a tag/category filter refetches `items`, and previously-checked cards that
      // don't match the new filter would otherwise disappear from the selection.
      const filterItem = multi_select
        ? selectedItemsMultiSelect.current
        : items.filter((ele: any) => ele?.is_checked);

      if (filterItem.length > 0) {
        const orderedFilteredItem = [...filterItem].sort((a: any, b: any) => {
          const aIndex = selectedItemsMultiSelect.current.findIndex((item: any) =>
            isSameGalleryItem(item, a),
          );
          const bIndex = selectedItemsMultiSelect.current.findIndex((item: any) =>
            isSameGalleryItem(item, b),
          );
          return aIndex - bIndex;
        });
        handleSelectedContent({ slots: orderedFilteredItem, selectedFilter }, name);
      } else if (allowEmptySelection) {
        // No items selected - fall back to query behavior (only for hybrid query flows)
        const queryParam = {
          filter: filter,
          cdpFilter: selectedCDPTagsRef.current,
          tags: selectedTagsRef.current,
          searchTerm: search || "",
          pagination: {
            start: 0,
            rows: prelemId === "Prelem_053" ? 5 : 4,
          },
        };
        handleSelectedContent({ slots: items, queryParam, selectedFilter });
      }
      onToggleContentGallery(-1);
    } else {
      const queryParam = {
        filter: filter,
        cdpFilter: selectedCDPTagsRef.current,
        tags: selectedTagsRef.current,
        searchTerm: search || "",
        pagination: {
          start: 0,
          rows: prelemId === "Prelem_053" ? 5 : 4,
        },
      };
      handleSelectedContent({ slots: items, queryParam, selectedFilter });
    }
  };

  const getSearchSuggestions = (fil, sear) => {
    fetchMultiSlotContentList({
      variables: {
        filter: fil,
        cdpFilter: selectedCDPTagsRef.current,
        tags: selectedTagsRef.current,
        searchTerm: sear,
        pagination: { start: 0, rows: rows },
        isSuggestive: true,
      },
    })
      .then((res) => {
        if (res?.data?.authoring_getDynamicContentSearch) {
          // add extra key value pair
          const pairAdded = customPairAdded(
            res?.data?.authoring_getDynamicContentSearch,
            multi_select,
            single_select,
          );

          setSearchItems(pairAdded);
          setSuggestionEnabled(true);
        }
      })
      .catch(() => {})
      .finally(() => {
        setLoading(false);
      });
  };

  const getAllContentTypes = (fil, sear = "") => {
    setLoading(true);
    setStartIndex(0);
    runContentFetch({
      filter: fil,
      cdpFilter: selectedCDPTagsRef.current,
      tags: selectedTagsRef.current,
      searchTerm: sear,
      pagination: { start: 0, rows: rows },
      isSuggestive: false,
    })
      .then((res) => {
        if (res?.data?.authoring_getDynamicContentSearch) {
          setLoading(false);
          // add extra key value pair
          const pairAdded = customPairAdded(
            res?.data?.authoring_getDynamicContentSearch,
            multi_select,
            single_select,
          );
          // setItems(res?.data?.authoring_getDynamicContentSearch);

          if (single_select || multi_select) {
            // Match against the live accumulated ref (not the stale `selectedItems` prop) so
            // items checked earlier THIS session under a different tag/filter still come back
            // marked is_checked once refetched - the prop never updates mid-session.
            const matchSource = multi_select ? selectedItemsMultiSelect.current : selectedItems;
            const newShallowArray = pairAdded.map((val: any) => {
              const matchedIndex = matchSource?.findIndex((item: any) =>
                isSameGalleryItem(item, val),
              );
              return matchedIndex > -1
                ? { ...val, is_checked: true, selectedIndex: matchedIndex }
                : val;
            });
            matchSource?.length > 0 ? setItems(newShallowArray) : setItems(pairAdded);
          } else {
            setItems(pairAdded);
          }

          if (res?.data?.authoring_getDynamicContentSearch?.length < 18) {
            setIsLazyLoad(false);
          } else {
            setIsLazyLoad(true);
          }
        } else {
          setLoading(false);
        }
      })
      .catch(() => {})
      .finally(() => {
        setLoading(false);
      });
  };

  const fetchMoreContentTypes = (fil, sear, index) => {
    setLoading(true);
    runContentFetch({
      filter: fil,
      cdpFilter: selectedCDPTagsRef.current,
      tags: selectedTagsRef.current,
      searchTerm: sear || "",
      pagination: { start: index, rows: rows },
      isSuggestive: false,
    })
      .then((res) => {
        if (res?.data?.authoring_getDynamicContentSearch) {
          // add extra key value pair
          const pairAdded = customPairAdded(
            res?.data?.authoring_getDynamicContentSearch,
            multi_select,
            single_select,
          );
          // const newData = [...items, ...(res?.data?.authoring_getDynamicContentSearch || {})];
          const newData = [...items, ...(pairAdded || {})];

          if (single_select || multi_select) {
            const matchSource = multi_select ? selectedItemsMultiSelect.current : selectedItems;
            const newShallowArray = newData.map((val: any) => {
              const matchedIndex = matchSource?.findIndex((item: any) =>
                isSameGalleryItem(item, val),
              );
              return matchedIndex > -1
                ? { ...val, is_checked: true, selectedIndex: matchedIndex }
                : val;
            });
            matchSource?.length > 0 ? setItems(newShallowArray) : setItems(() => newData);
          } else {
            setItems(() => newData);
          }

          if (res?.data?.authoring_getDynamicContentSearch?.length < 18) {
            setIsLazyLoad(false);
          } else {
            setIsLazyLoad(true);
          }
        }
        setLoading(false);
      })
      .catch((error: any) => {
        const statusCode = error?.networkError?.statusCode || 0;
        ShowToastError(t("api_error_toast"), statusCode);
        setLoading(false);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const removeCDPTag = (tagValue: string): boolean => {
    if (selectedCDPTagsRef.current.length <= 1) {
      ShowToastError("At least one Content/Collection must be selected", 0);
      return false;
    }
    applyCDPTags(selectedCDPTagsRef.current.filter((tag: string) => tag !== tagValue));
    return true;
  };

  const handleTagOnChange = (event) => {
    try {
      const { id: categoryKey, value: tagValue, checked } = event.target;

      if (["Collection", "Content Type"].includes(categoryKey)) {
        if (checked) {
          applyCDPTags([...selectedCDPTagsRef.current, tagValue]);
        } else if (!removeCDPTag(tagValue)) {
          return;
        }
      } else {
        if (checked) {
          applyTags([...selectedTagsRef.current, tagValue]);
        } else {
          applyTags(selectedTagsRef.current.filter((tag: string) => tag !== tagValue));
        }
      }

      // Refresh content with updated tags
      getAllContentTypes(filter, search);
    } catch (error) {
      ShowToastError(t("tag_update_error") || "Failed to update tags", 0);
    }
  };

  const handleRemoveCDPTagChip = (tagValue: string) => {
    if (removeCDPTag(tagValue)) {
      getAllContentTypes(filter, search);
    }
  };

  const handleRemoveTagChip = (tagValue: string) => {
    applyTags(selectedTagsRef.current.filter((tag: string) => tag !== tagValue));
    getAllContentTypes(filter, search);
  };

  // Sort / filter application. Each writes its ref before refetching, because
  // runContentFetch reads `*Ref.current` at call time.
  const handleApplySort = (nextSort: GallerySortValue) => {
    sortValueRef.current = nextSort;
    setSortValue(nextSort);
    getAllContentTypes(filter, search);
  };

  const handleClearSort = () => {
    sortValueRef.current = null;
    setSortValue(null);
    getAllContentTypes(filter, search);
  };

  const handleApplyFilter = (nextFilter: GalleryFilterValue) => {
    filterValueRef.current = nextFilter;
    setFilterValue(nextFilter);
    getAllContentTypes(filter, search);
  };

  const handleRemoveDateRangeChip = () => {
    handleApplyFilter({ ...filterValueRef.current, fromDate: "", toDate: "" });
  };

  const handleRemoveAuthorChip = (author: string) => {
    handleApplyFilter({
      ...filterValueRef.current,
      author: (filterValueRef.current.author || []).filter((applied: string) => applied !== author),
    });
  };

  const handleClearFilters = () => {
    handleApplyFilter({ ...DEFAULT_GALLERY_FILTER });
  };

  // const clearTag = () => {
  //   setselectedTag([]);
  //   tagRef.current = { ...tagRef?.current, tags: [] };
  //   setTagObj({});
  //   getTagData();
  //   getAllContentTypes(filter, search);
  // };
  const fetchMoreData = () => {
    const nextIndex = startIndex + rows;
    setStartIndex(() => nextIndex);
    fetchMoreContentTypes(filter, search, nextIndex);
  };

  const multiSelectedHandle = (_, index) => {
    const newShallowArray = [...items];
    const newObj: any = newShallowArray?.[index];
    newObj.is_checked = !newObj?.is_checked;
    newShallowArray[index] = newObj;
    if (newObj?.is_checked) {
      // The same content can legitimately appear more than once in `items` (pagination can
      // overlap the same item across two fetched pages when the underlying list shifts between
      // requests) - guard against pushing a second ref entry for content already selected via
      // its other occurrence, which would otherwise save as a duplicate.
      const alreadySelected = selectedItemsMultiSelect.current.some((item: any) =>
        isSameGalleryItem(item, newObj),
      );
      if (!alreadySelected) {
        selectedItemsMultiSelect.current.push(newObj);
      }
    } else {
      // Strict CurrentPageURL equality doesn't reliably remove seeded/stored items: their
      // CurrentPageURL can differ in format from a freshly-fetched item's (see
      // isSameGalleryItem's comment) even when they're the same content. Match the same way
      // everything else in this file does.
      selectedItemsMultiSelect.current = selectedItemsMultiSelect.current.filter(
        (item) => !isSameGalleryItem(item, newObj),
      );
    }
    setItems(newShallowArray);
  };
  const singleSelectedHandle = (_, index) => {
    const newShallowArray = items.map((item, i) => {
      if (i !== index && item?.is_checked) {
        return { ...item, is_checked: false };
      }
      return item;
    });
    const newObj: any = newShallowArray?.[index];
    newObj.is_checked = !newObj?.is_checked;
    newShallowArray[index] = newObj;
    setItems(newShallowArray);
  };
  const handleSearchChange = useCallback(
    debounce((fil, sear) => getSearchSuggestions(fil, sear)),
    [],
  );

  useEffect(() => {
    if (Object.keys(tagData).length === 0) {
      getTagData();
    }
    const closeDropdown: Parameters<Document["addEventListener"]>[1] = (event: any) => {
      const hasClass = event?.target?.classList.contains("suggestionitems");
      if (!hasClass) {
        setSuggestionEnabled(false);
      }
    };
    document.addEventListener("click", closeDropdown);
    return () => {
      document.removeEventListener("click", closeDropdown);
    };
  }, []);

  // Enhanced useEffect for QueryParam type with proper tagObjRef.current building
  useEffect(() => {
    // This effect gets triggered on page load.
    if (tagData?.length > 0) {
      const initalTags = restrictToAvailableCDPTags
        ? getAvailableContentAndCollectionTags()
        : getInitialContentAndCollectionTags();
      setAllowedCDPTags(initalTags); // TODO: this should be selectedCDPTags from API (new property)
      // Check if selected_filter has tags, else use initalTags
      if (selectedCDPTags?.length === 0) {
        setSelectedCDPTags(initalTags);
        selectedCDPTagsRef.current = initalTags;
      }
      getAllContentTypes(filter, search);
    }
  }, [tagData]);

  const clearSelection = () => {
    const newShallowItems = [...items];
    newShallowItems.forEach((item) => {
      item.is_checked = false;
    });
    selectedItemsMultiSelect.current = [];
    setItems(newShallowItems);
  };

  const categoryClickHandle = () => {
    if (isShowItems) {
      setShowItems(false);
    } else {
      setShowItems(true);
    }
    if (isShowCategory) {
      setShowCategory(false);
    } else {
      setShowCategory(true);
    }
  };

  const backButtonHandle = () => {
    setShowCategory(false);
    setShowItems(true);
  };
  const onSearchChange = (e) => {
    const { value } = e.currentTarget;
    setSearch(value);
    if (value.length > 3) {
      handleSearchChange(filter, value);
    }
  };
  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      getAllContentTypes(filter, e.target.value);
      setSearch(e.target.value);
      setSuggestionEnabled(false);
    }
  };

  const resetSearch = () => {
    setSearch("");

    setSearchItems([]);
    setSuggestionEnabled(false);
    getAllContentTypes(filter, "");
  };

  const clearAllFilters = () => {
    const unnarrowedCDPTags =
      allowedCDPTags?.length > 0 ? [...allowedCDPTags] : selectedCDPTagsRef.current;
    applyCDPTags(unnarrowedCDPTags);
    applyTags([]);
    setSearch("");
    setSearchItems([]);
    setSuggestionEnabled(false);
    getAllContentTypes(filter, "");
  };

  // const handleFilterChange = (e) => {
  //   const { value } = e.target;
  //   setFilter(value);
  // };

  const handlePageSearch = (pageSearchName) => {
    setSearch(pageSearchName);
    getAllContentTypes(filter, pageSearchName);
    setSearchItems([]);
  };

  const getSelectionMessage = () => {
    switch (true) {
      case Boolean(single_select):
        return "You can select only one Card.";
      case Boolean(multi_select):
        return "You can select multiple Cards.";
      default:
        return "You cannot select Cards, but the prelem will display results based on query.";
    }
  };

  /**
   * Thumbnail/list switch. Rendered into BOTH the mobile and the >=em (1024px) header - the
   * two are separate render branches, so a control added to only one silently disappears at
   * the other's breakpoints.
   *
   * TODO(KIWI-6316): the Sort control belongs beside this toggle. It is blocked on backend
   * confirming that `authoring_getDynamicContentSearch` accepts `sort`/`sortOptions` - the
   * date-field shape itself is settled (`{ date: ASC|DESC, date_type:
   * published|created|modified }`) but is only confirmed on `authoring_getContentTypeItems`,
   * which this screen cannot use (it spans several content types via `cdpFilter`). See
   * docs/features/KIWI-6316/02-breakdown.md.
   */
  const activeFilterCount = countActiveFilters(filterValue);

  const renderToolbarButton = ({
    testId,
    label,
    ariaLabel,
    icon,
    kind,
    active,
    onActivate,
    badge,
    showDot,
    disabled,
    disabledTitle,
  }: {
    testId: string;
    label: string;
    ariaLabel?: string;
    icon: React.ReactNode;
    kind: "toggle" | "dialog";
    active: boolean;
    onActivate: () => void;
    badge?: number;
    showDot?: boolean;
    disabled?: boolean;
    disabledTitle?: string;
  }) => (
    <Box
      className={`${classes.toolbarButton} ${active ? "active" : ""} ${disabled ? "disabled" : ""}`}
      role='button'
      tabIndex={disabled ? -1 : 0}
      aria-label={ariaLabel || label}
      aria-disabled={disabled || undefined}
      aria-pressed={kind === "toggle" ? active : undefined}
      aria-haspopup={kind === "dialog" ? "dialog" : undefined}
      aria-expanded={kind === "dialog" ? active : undefined}
      title={disabled ? disabledTitle : undefined}
      data-testid={testId}
      onClick={disabled ? undefined : onActivate}
      onKeyDown={(event) => {
        if (disabled) return;
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          onActivate();
        }
      }}>
      {icon}
      {/* p3regular is the design's Inter / 400 / 14px. The theme variant also hardcodes
          `color: #4E4B66`, which would leave the label grey on the dark active fill, so
          `toolbarButtonLabel` forces it to inherit the button's own colour. */}
      <Typography
        variant='p3regular'
        className='toolbarButtonLabel'
        sx={{ display: { xs: "none", md: "inline" } }}>
        {label}
      </Typography>
      {badge && badge > 0 && !disabled ? (
        <Box component='span' className='toolbarButtonBadge' data-testid={`${testId}-badge`}>
          {badge}
        </Box>
      ) : null}
      {showDot && !disabled ? (
        <Box component='span' className='toolbarButtonDot' data-testid={`${testId}-dot`} />
      ) : null}
    </Box>
  );

  const renderGalleryToolbar = (variant: "mobile" | "desktop") => (
    <Box className={classes.galleryToolbar} data-testid={`gallery-toolbar-${variant}`}>
      {renderToolbarButton({
        testId: `gallery-view-thumbnail-${variant}`,
        label: t("thumbnail"),
        ariaLabel: t("thumbnail_view"),
        icon: <ViewCozyOutlinedIcon sx={{ fontSize: "18px" }} />,
        kind: "toggle",
        active: viewMode === GALLERY_VIEW_MODES.THUMBNAIL,
        onActivate: () => setViewMode(GALLERY_VIEW_MODES.THUMBNAIL),
      })}
      {renderToolbarButton({
        testId: `gallery-view-list-${variant}`,
        label: t("list"),
        ariaLabel: t("list_view"),
        icon: <ViewAgendaOutlinedIcon sx={{ fontSize: "18px" }} />,
        kind: "toggle",
        active: viewMode === GALLERY_VIEW_MODES.LIST,
        onActivate: () => setViewMode(GALLERY_VIEW_MODES.LIST),
      })}
      {renderToolbarButton({
        testId: `gallery-filter-button-${variant}`,
        label: t("filter"),
        icon: <FilterAltOutlinedIcon sx={{ fontSize: "18px" }} />,
        kind: "dialog",
        active: isFilterOpen,
        onActivate: () => setFilterOpen(true),
        badge: activeFilterCount,
        disabled: isSortFilterUnsupported,
        disabledTitle: t("sort_filter_unsupported"),
      })}
      {renderToolbarButton({
        testId: `gallery-sort-button-${variant}`,
        label: t("sort"),
        icon: <SwapVertIcon sx={{ fontSize: "18px" }} />,
        kind: "dialog",
        active: isSortOpen,
        onActivate: () => setSortOpen(true),
        showDot: Boolean(sortValue),
        disabled: isSortFilterUnsupported,
        disabledTitle: t("sort_filter_unsupported"),
      })}
    </Box>
  );

  return (
    <Box
      sx={{
        position: "absolute",
        backgroundColor: "#fff",
        width: "100%",
        height: "100vh",
        zIndex: "10",
      }}>
      {loading && <Loader />}
      {isHideSearchBox && (
        <Grid
          container
          sx={{
            display: { xs: "flex", em: "none" },
            padding: "10px",
          }}>
          <Grid
            item
            xs={10}
            sm={10}
            sx={{
              margin: "auto 0",
              paddingBottom: { xs: "20px", sm: "20px" },
            }}>
            <FormControl fullWidth>
              <TextField
                sx={{
                  backgroundColor: "#f5f6f8",
                  height: "40px",
                  ".Platform-x-Input-root:before": {
                    borderBottom: "2px solid #2d2d39",
                  },
                  ".Platform-x-Input-root:after": {
                    borderBottom: "2px solid #000000",
                  },
                  ".Platform-x-Input-root.Mui-disabled:before": {
                    borderBottom: "2px solid #c3c3cb",
                  },
                  "Platform-x-OutlinedInput-root": {
                    borderTopRightRadius: "0px",
                    borderBottomRightRadius: "0px",
                  },
                }}
                variant='filled'
                placeholder={t("search")}
                value={search}
                onChange={onSearchChange}
                onKeyPress={handleKeyPress}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position='start'>
                      <BackArrow handleReturn={backToContentHandel} />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position='end'>
                      {search && (
                        <CloseRoundedIcon
                          onClick={resetSearch}
                          sx={{
                            cursor: "pointer",
                            position: "absolute",
                            right: "18px",
                          }}
                        />
                      )}
                    </InputAdornment>
                  ),
                }}
              />
            </FormControl>
          </Grid>
          <Grid
            item
            xs={2}
            sm={1}
            sx={{
              margin: "auto 0",
              paddingBottom: { xs: "20px", sm: "20px" },
              display: "flex",
            }}></Grid>
        </Grid>
      )}
      {!isHideSearchBox && (
        <Grid
          container
          sx={{
            display: { xs: "flex", em: "none" },
            padding: "15px",
          }}>
          <Grid
            item
            xs={6}
            sm={7}
            sx={{
              margin: "auto 0",
              paddingBottom: { xs: "20px", sm: "20px" },
              display: "flex",
              alignItems: "center",
            }}>
            <BackArrow handleReturn={() => onToggleContentGallery(-1)} />
            <Typography variant='h4bold'>{t("prelem_choose_content")}</Typography>
          </Grid>
          <Grid
            item
            xs={6}
            sm={5}
            sx={{
              margin: "auto 0",
              paddingBottom: { xs: "0px", sm: "20px" },
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-end",
            }}>
            {renderGalleryToolbar("mobile")}
            <Typography
              variant='h5regular'
              sx={{
                marginRight: "10px",
                marginLeft: "10px",
                display: "flex",
                alignItems: "center",
                cursor: "pointer",
              }}
              role='button'
              tabIndex={0}
              aria-label={t("search")}
              onClick={mobileSearchIconHandel}
              onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();
                  mobileSearchIconHandel();
                }
              }}>
              <SearchIcon />
            </Typography>
            <Typography
              variant='h5regular'
              sx={{ cursor: "pointer", display: "flex", alignItems: "center" }}
              role='button'
              tabIndex={0}
              aria-label={t("categories")}
              onClick={categoryClickHandle}
              onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();
                  categoryClickHandle();
                }
              }}>
              {selectedCDPTags?.length > 0 ? (
                <Badge badgeContent={selectedCDPTags.length} color='error'>
                  <TagIcon />
                </Badge>
              ) : (
                <TagIcon />
              )}
            </Typography>
          </Grid>
        </Grid>
      )}
      <Grid
        container
        sx={{
          display: { xs: "none", em: "flex" },
          padding: "10px 15px",
          // height: "70px",
          alignItems: "center",
          justifyContent: "center",
          borderBottom: "1px solid #d9dbe9",
        }}>
        <Grid
          item
          xs={12}
          sm={12}
          em={3}
          lg={3}
          sx={{
            margin: "auto 0",
            display: "flex",
            alignItems: "center",
          }}>
          <BackArrow handleReturn={() => onToggleContentGallery(-1)} />
          <Typography variant='h4bold'>{t("prelem_choose_content")}</Typography>
        </Grid>

        <Grid
          item
          xs={12}
          sm={12}
          em={5}
          lg={6}
          sx={{
            padding: {
              xs: "10px 0",
              sm: "0",
              display: "flex",
              justifyContent: "center",
            },
          }}>
          <Grid item xs={12} sm={12} em={12} lg={9}>
            <FormControl fullWidth>
              <TextField
                className='contentTypeCard'
                variant='filled'
                placeholder={t("search")}
                value={search}
                onChange={onSearchChange}
                inputProps={{ style: { textAlign: "left" } }}
                onKeyPress={handleKeyPress}
                sx={{
                  ".Platform-x-InputBase-root.Platform-x-OutlinedInput-root": {
                    borderLeft: 0,
                    height: "50px",
                    border: "solid 1px #D9DBE9 !important",
                  },
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position='start' style={{ marginLeft: "15px" }}>
                      <img
                        src={searchIcon}
                        alt='search'
                        style={{ height: "20.25px", width: "20.25px" }}
                      />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position='end'>
                      {search && (
                        <CloseRoundedIcon
                          onClick={resetSearch}
                          sx={{
                            cursor: "pointer",
                            position: "absolute",
                            right: "18px",
                          }}
                        />
                      )}
                    </InputAdornment>
                  ),
                }}
              />
            </FormControl>
          </Grid>
        </Grid>

        <Grid
          item
          xs={12}
          sm={12}
          em={4}
          lg={3}
          container
          spacing={0}
          direction='column'
          alignItems='end'
          justifyContent='center'
          sx={{ display: "flex" }}>
          <Box justifyContent='end'>
            <Button
              variant='secondaryButton'
              className='sm'
              onClick={() => onToggleContentGallery(-1)}>
              {t("cancel")}
            </Button>
            <Button
              variant='primaryButton'
              className='sm'
              sx={{
                marginLeft: "12px",
              }}
              disabled={
                (multi_select || single_select) &&
                !allowEmptySelection &&
                (multi_select
                  ? selectedItemsMultiSelect.current.length === 0
                  : !items.some((ele: any) => ele?.is_checked))
              }
              onClick={handleDone}>
              {t("save")}
            </Button>
          </Box>
        </Grid>
      </Grid>

      <Grid container spacing={0}>
        {isShowCategory && (
          <Grid
            container
            xs={12}
            em={3}
            xl={2}
            lg={2}
            sx={{ display: { xs: "block", em: "none" } }}>
            <Box
              sx={{
                padding: "15px",
                backgroundColor: "#ced3d9",
              }}>
              <Stack direction='row' alignItems='center' gap={1}>
                <Box component='span'>
                  <BackArrow handleReturn={backButtonHandle} />
                </Box>
                <Typography variant='h4medium' component='span' sx={{ textTransform: "uppercase" }}>
                  {t("categories")}
                </Typography>
                <Box
                  component='span'
                  sx={{
                    margin: "0px 15px 0px 0",
                    right: "0",
                    position: "absolute",
                  }}>
                  <SearchIcon />
                </Box>
              </Stack>
            </Box>
            {tagData &&
              tagData?.length > 0 &&
              tagData.map((categories, index) => {
                return (
                  <GalleryLeftSideBar
                    key={index}
                    categoriesdata={categories}
                    updateTagField={handleTagOnChange}
                    selectedCDPTags={selectedCDPTags}
                    selectedTags={selectedTags}
                    allowed_collection={allowed_collection}
                    allowed_content={allowed_content}
                    allowedCDPTags={allowedCDPTags}
                  />
                );
              })}
          </Grid>
        )}

        <Grid
          container
          xs={12}
          em={3}
          xl={2.5}
          lg={2.5}
          sx={{
            overflowY: "scroll",
            overflowX: "hidden",
            height: "calc(100vh - 100px)",
            display: { xs: "none", em: "block" },
          }}>
          <Box
            sx={{
              padding: "15px",
              justifyContent: "space-between",
              display: "flex",
              backgroundColor: "#F7F7FC",
            }}>
            <Typography variant='h6semibold'>{capitalizeFirstLetter(t("categories"))}</Typography>
          </Box>
          {tagData &&
            tagData?.length > 0 &&
            (() => {
              const priority = ["Content Type", "Collection"];
              const orderedCategories = [
                ...tagData.filter((c) => c?.category === "Content Type"),
                ...tagData.filter((c) => c?.category === "Collection"),
                ...tagData.filter((c) => !priority.includes(c?.category)),
              ];

              return orderedCategories.map((categories, index) => (
                <Fragment key={index}>
                  {index === 2 ? <Divider sx={{ margin: "10px 20px" }} /> : null}
                  <GalleryLeftSideBar
                    categoriesdata={categories}
                    updateTagField={handleTagOnChange}
                    selectedCDPTags={selectedCDPTags}
                    selectedTags={selectedTags}
                    allowed_collection={allowed_collection}
                    allowed_content={allowed_content}
                    allowedCDPTags={allowedCDPTags}
                  />
                </Fragment>
              ));
            })()}
        </Grid>

        {isShowItems && (
          <Grid
            item
            xs={12}
            em={9}
            xl={9.5}
            lg={9.5}
            sx={{
              backgroundColor: theme?.palette?.customColor?.BGLIGHT,
              // border: "1px solid #ccc",
            }}>
            {/* <Box sx={{ padding: "10px 10px 0 10px" }}>
              <Typography variant='h6semibold'>
                Displaying results for Type
                {selectedCDPTags?.length > 0 ? " (" + selectedCDPTags?.join(", ") + ")" : "..."}
                {""}
                {selectedTags?.length > 0 ? " AND Supporting Tags" : ""}
                {""}
                {selectedTags?.length > 0 ? "(" + selectedTags?.join(", ") + ")" : ""}.
              </Typography>
            </Box> */}
            <Box
              sx={{
                padding: "14px 20px 10px 10px",
                display: "flex",
                alignItems: "flex-start",
                justifyContent: "center",
                flexDirection: "column",
                gap: "8px",
              }}>
              <Box
                sx={{
                  width: "100%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: "12px",
                }}>
                <Typography
                  variant='h6semibold'
                  sx={{
                    display: {
                      xs: "none",
                      em: "block",
                    },
                  }}>
                  {getSelectionMessage()}
                </Typography>
                <Box
                  sx={{
                    display: { xs: "none", em: "flex" },
                    alignItems: "center",
                    marginLeft: "auto",
                  }}>
                  {renderGalleryToolbar("desktop")}
                </Box>
              </Box>

              <GalleryFilterChips
                selectedCDPTags={selectedCDPTags}
                selectedTags={selectedTags}
                searchTerm={search}
                sortValue={sortValue}
                filterValue={filterValue}
                onRemoveCDPTag={handleRemoveCDPTagChip}
                onRemoveTag={handleRemoveTagChip}
                onRemoveSearch={resetSearch}
                onRemoveDateRange={handleRemoveDateRangeChip}
                onRemoveAuthor={handleRemoveAuthorChip}
                onRemoveSort={handleClearSort}
                onClearAll={clearAllFilters}
                onClearFilters={handleClearFilters}
              />

              {selectedItemsMultiSelect.current?.length > 0 ? (
                <Box sx={{ display: "flex", alignItems: "center", marginTop: "8px" }}>
                  <Typography variant='h6semibold'>
                    No. of Selected Items:{" "}
                    <span className={classes.activeColor}>
                      {selectedItemsMultiSelect.current.length}
                    </span>
                  </Typography>
                  <Box
                    component='span'
                    sx={{
                      float: "right",
                      display: "inline-block",
                      cursor: "pointer",
                      fontSize: "14px",
                      marginLeft: "10px",
                      color: theme?.palette?.customColor?.TERTIARY,
                      textDecoration: "underline",
                    }}
                    role='button'
                    tabIndex={0}
                    onClick={clearSelection}
                    onKeyDown={(event) => {
                      if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        clearSelection();
                      }
                    }}>
                    Clear
                  </Box>
                </Box>
              ) : null}
            </Box>

            <Box
              sx={{
                paddingRight: "0px",
                paddingLeft: "0px",
                margin: "0px",
              }}>
              {viewMode === GALLERY_VIEW_MODES.LIST ? (
                <GalleryListItems
                  loading={loading}
                  galleryObj={items}
                  isLazyLoad={isLazyLoad}
                  multi_select={multi_select}
                  fetchMoreData={fetchMoreData}
                  multiSelectedHandle={multiSelectedHandle}
                  singleSelectedHandle={singleSelectedHandle}
                  single_select={single_select}
                  selectedmultiSelect={selectedItemsMultiSelect.current}
                />
              ) : (
                <GalleryItems
                  loading={loading}
                  galleryObj={items}
                  isLazyLoad={isLazyLoad}
                  multi_select={multi_select}
                  fetchMoreData={fetchMoreData}
                  multiSelectedHandle={multiSelectedHandle}
                  singleSelectedHandle={singleSelectedHandle}
                  single_select={single_select}
                  selectedmultiSelect={selectedItemsMultiSelect.current}
                />
              )}
            </Box>
            {isSuggestionEnabled && searchItems?.length > 0 ? (
              <Box
                sx={{
                  position: {
                    xs: "absolute",
                    sm: "absolute",
                    em: "absolute",
                  },
                  top: "70px",
                  left: {
                    xs: "33px",
                    sm: "33px",
                    em: "220px",
                    lg: "415px",
                  },
                  width: { xs: "70%", sm: "70%", em: "46%", lg: "32%" },
                  zIndex: "999999",
                }}
                className='searchsuggestion'>
                <GalleryItemSearchResults
                  searchResults={searchItems}
                  handlePageSearch={handlePageSearch}
                />
              </Box>
            ) : null}
          </Grid>
        )}
      </Grid>
      <Box
        sx={{
          display: { xs: isShowCategory ? "none" : "block", em: "none" },
          position: "fixed",
          bottom: 0,
          right: 0,
          zIndex: "9999999",
        }}>
        <Box sx={{ margin: "0 25px 25px 0" }} onClick={handleDone}>
          <Fab size='large' color='primary' aria-label='add'>
            <DoneIcon style={{ color: "#fff" }} />
          </Fab>
        </Box>
      </Box>

      <GallerySortModal
        open={isSortOpen}
        onClose={() => setSortOpen(false)}
        value={sortValue}
        onApply={handleApplySort}
        onClear={handleClearSort}
      />

      <GalleryFilterModal
        open={isFilterOpen}
        onClose={() => setFilterOpen(false)}
        value={filterValue}
        onApply={handleApplyFilter}
      />
    </Box>
  );
};

export default DynamicContentGallery;
